import { useToastContext } from '@rtkwlf/fenrir-react';
import type { CredentialPage } from '@rtkwlf/rendall';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useDeleteCredentialedScan } from '@apis/rendall/queries/use-delete-credentialed-scan';
import { getScannerScanCredentialsQueryKey } from '@apis/rendall/queries/use-scanner-scan-credentials/use-scanner-scan-credentials';
import { useOrganizationContext } from '@packages/legacy/organization-context';
import { TOAST_DURATION } from 'ae-constants/toast';

export const useDeleteCredentialedScanWrapper = (scannerId: string) => {
  const { selectedOrganization } = useOrganizationContext();
  const { t } = useTranslation('telemetryScannerConfig');
  const { addToast } = useToastContext();
  const queryClient = useQueryClient();
  const queryKey = getScannerScanCredentialsQueryKey(
    selectedOrganization.id,
    scannerId,
  );

  return useDeleteCredentialedScan(scannerId, {
    gcTime: 0,
    onError: (_err, _variables, context) => {
      addToast({
        appearance: 'danger',
        duration: TOAST_DURATION,
        message: t('credentialedScanning.error'),
        title: t('notifications.error'),
      });
      if (context?.dataSnapshot) {
        queryClient.setQueryData<CredentialPage>(
          queryKey,
          context.dataSnapshot,
        );
      }
    },
    onMutate: async (credentialId) => {
      await queryClient.cancelQueries({
        queryKey,
      });

      const dataSnapshot = queryClient.getQueryData<CredentialPage>(queryKey);

      queryClient.setQueryData(queryKey, (prevData: CredentialPage) => ({
        ...prevData,
        items: [
          ...prevData.items.filter(
            (item) => item.id !== credentialId.toString(),
          ),
        ],
      }));

      return { dataSnapshot };
    },
    onSuccess: () => {
      addToast({
        appearance: 'success',
        duration: TOAST_DURATION,
        message: t('credentialedScanning.success'),
        title: t('notifications.success'),
      });
    },
  });
};
